
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useCallback, useRef } from 'react';
import useAuth from '@/hooks/useAuth';
import { FormStatus, Notification } from '@/types';
import * as firebaseApi from '@/services/firebaseApi';

const useNotifications = () => {
    const queryClient = useQueryClient();
    const { accountPublicData } = useAuth();
    const userId = accountPublicData?.id;

    // State for filtering (future extensibility)
    const [filterQuery, setFilterQuery] = useState('');
    const [displayRead, setDisplayRead] = useState(true);

    // Query for notifications
    const {
        data: notifications = [],
        isLoading,
        isFetching,
        isError,
        refetch: refetchNotifications
    } = useQuery({
        queryKey: ['notifications', userId],
        queryFn: () => userId ? firebaseApi.getNotifications(userId) : Promise.resolve([]),
        enabled: !!userId,
        staleTime: 2 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    // Helper for status
    const getStatus = useCallback((loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    }, []);

    // Filtering (by query and read/unread)
    const filteredNotifications = useMemo(() => {
        let list = [...notifications];
        if (!displayRead) {
            list = list.filter((n: Notification) => !n.isRead);
        }
        if (filterQuery) {
            const q = filterQuery.toLowerCase();
            list = list.filter((n: Notification) =>
                n.title?.toLowerCase().includes(q) ||
                n.message?.toLowerCase().includes(q)
            );
        }
        return list;
    }, [notifications, filterQuery, displayRead]);

    // Unread count
    const unreadCount = useMemo(() => notifications.filter((n: Notification) => !n.isRead).length, [notifications]);

    const notificationsQueryKey = useMemo(() => ['notifications', userId] as const, [userId]);

    // Tracks the latest requested read state per notification so a newer click
    // is not overwritten by an older request finishing or failing.
    const readSyncRef = useRef(new Map<string, { desired: boolean; confirmed: boolean; running: boolean }>());

    const setNotificationRead = useCallback((notificationId: string, isRead: boolean) => {
        queryClient.setQueryData<Notification[]>(notificationsQueryKey, (current = []) =>
            current.map((notification) =>
                notification.id === notificationId ? { ...notification, isRead } : notification
            )
        );
    }, [queryClient, notificationsQueryKey]);

    const markAsReadMutation = useMutation({
        mutationFn: (notificationId: string) => firebaseApi.markNotificationAsRead(notificationId),
    });

    const markAsUnreadMutation = useMutation({
        mutationFn: (notificationId: string) => firebaseApi.markNotificationAsUnread(notificationId),
    });

    const deleteNotificationMutation = useMutation({
        mutationFn: (notificationId: string) => firebaseApi.deleteNotification(notificationId),
    });

    const syncNotificationRead = useCallback(async (notificationId: string, isRead: boolean) => {
        if (!userId || !notificationId) return { status: FormStatus.ERROR };

        await queryClient.cancelQueries({ queryKey: notificationsQueryKey });

        let sync = readSyncRef.current.get(notificationId);
        if (!sync) {
            const current = queryClient.getQueryData<Notification[]>(notificationsQueryKey);
            const confirmed = current?.find((notification) => notification.id === notificationId)?.isRead ?? !isRead;
            sync = { desired: isRead, confirmed, running: false };
            readSyncRef.current.set(notificationId, sync);
        } else {
            sync.desired = isRead;
        }

        setNotificationRead(notificationId, isRead);

        if (sync.running) return { status: FormStatus.SUCCESS };

        sync.running = true;
        let failed = false;
        try {
            while (sync.desired !== sync.confirmed) {
                const target = sync.desired;
                setNotificationRead(notificationId, target);
                try {
                    if (target) await markAsReadMutation.mutateAsync(notificationId);
                    else await markAsUnreadMutation.mutateAsync(notificationId);
                    sync.confirmed = target;
                    if (sync.desired === target) setNotificationRead(notificationId, target);
                } catch {
                    if (sync.desired !== target) continue;
                    setNotificationRead(notificationId, sync.confirmed);
                    sync.desired = sync.confirmed;
                    failed = true;
                    break;
                }
            }
        } finally {
            sync.running = false;
            if (sync.desired === sync.confirmed) {
                readSyncRef.current.delete(notificationId);
            }
        }

        return { status: failed ? FormStatus.ERROR : FormStatus.SUCCESS };
    }, [userId, queryClient, notificationsQueryKey, setNotificationRead, markAsReadMutation, markAsUnreadMutation]);

    const markAsRead = useCallback(async (notificationId: string) => {
        return syncNotificationRead(notificationId, true);
    }, [syncNotificationRead]);

    const markAsUnread = useCallback(async (notificationId: string) => {
        return syncNotificationRead(notificationId, false);
    }, [syncNotificationRead]);

    const deleteNotification = useCallback(async (notificationId: string) => {
        if (!userId || !notificationId) throw new Error('Missing user');

        await queryClient.cancelQueries({ queryKey: notificationsQueryKey });

        const previous = queryClient.getQueryData<Notification[]>(notificationsQueryKey) ?? [];
        const index = previous.findIndex((notification) => notification.id === notificationId);
        const removed = index >= 0 ? previous[index] : undefined;

        if (removed) {
            queryClient.setQueryData<Notification[]>(
                notificationsQueryKey,
                previous.filter((notification) => notification.id !== notificationId)
            );
        }

        try {
            await deleteNotificationMutation.mutateAsync(notificationId);
        } catch (error) {
            if (removed) {
                queryClient.setQueryData<Notification[]>(notificationsQueryKey, (current = []) => {
                    if (current.some((notification) => notification.id === notificationId)) return current;
                    const next = [...current];
                    next.splice(Math.min(Math.max(index, 0), next.length), 0, removed);
                    return next;
                });
            }
            throw error;
        }
    }, [userId, queryClient, notificationsQueryKey, deleteNotificationMutation]);

    // Status
    const notificationsStatus = getStatus(isLoading, isError);
    const markAsReadStatus = markAsReadMutation.isPending ? FormStatus.LOADING : (markAsReadMutation.isError ? FormStatus.ERROR : FormStatus.SUCCESS);
    const markAsUnreadStatus = markAsUnreadMutation.isPending ? FormStatus.LOADING : (markAsUnreadMutation.isError ? FormStatus.ERROR : FormStatus.SUCCESS);
    const deleteNotificationStatus = deleteNotificationMutation.isPending ? FormStatus.LOADING : (deleteNotificationMutation.isError ? FormStatus.ERROR : FormStatus.SUCCESS);

    // Return interface (pattern matching useProducts)
    return {
        notifications,
        filteredNotifications,
        notificationsStatus,
        isFetchingNotifications: isFetching,
        unreadCount,
        refetchNotifications,
        markAsRead,
        markAsUnread,
        deleteNotification,
        isDeleting: deleteNotificationMutation.isPending,
        markAsReadStatus,
        markAsUnreadStatus,
        deleteNotificationStatus,
        filterQuery,
        setFilterQuery,
        displayRead,
        setDisplayRead,
    };
};

export default useNotifications;
